import { useMutation, useQuery } from "@tanstack/react-query";

import api from "./apiClient";

import cookiesHelper from "../helpers/cookies";

import { IActiveUserReportData, IUserScoreReportData, IUserSignUpReportData, IInterviewReportData, IActiveUserDemographicsReportData, IUserJobsReportData, IUserVisitsReportData, IPageViewsReportData, ICertificateAwardsReportData, HMIExportUserSignUpReportForSchoolData, HMIExportCertificateAwardsReportForSchoolData, HMIExportActiveUserReportForSchoolData, HMIExportUserScoresReportForSchoolData, HMIExportInterviewReportForSchoolData, HMIExportActiveUserDemographicsReportForSchoolData, HMIExportUserJobsReportForSchoolData, HMIExportUserVisitsReportForSchoolData, HMIExportPageViewsReportForSchoolData } from "../interfaces/analytics";

const useGetUserSignUpReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<IUserSignUpReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/user-sign-up-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['user-sign-up-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useExportUserSignUpReportForSchool = () => {
    const mutationFn = async (variables: HMIExportUserSignUpReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/user-sign-up-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return res.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'user_sign_up_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

const useGetActiveUserReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<IActiveUserReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/active-user-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['active-user-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useGetUserScoreReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<IUserScoreReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/user-score-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['user-score-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useGetInterviewReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<IInterviewReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/interview-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['interview-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useGetActiveUserDemographicsReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<IActiveUserDemographicsReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/active-user-demographics-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['active-user-demographics-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useGetUserJobsReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<IUserJobsReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/user-jobs-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['user-jobs-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useGetUserVisitsReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<IUserVisitsReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/user-visits-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['user-visits-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useGetPageViewsReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<IPageViewsReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/page-views-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['page-views-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useGetCertificateAwardsReportForSchool = (filterQuery: string) => {
    const queryFn = async (): Promise<ICertificateAwardsReportData> => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const res = await api.get(`analytics/school/certificate-awards-report?${filterQuery}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['certificate-awards-report', filterQuery],
        queryFn,
        retry: 0,
    });
};

const useExportCertificateAwardsReportForSchool = () => {
    const mutationFn = async (variables: HMIExportCertificateAwardsReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`analytics/school/certificate-awards-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return response.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'certificate_awards_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

const useExportActiveUserReportForSchool = () => {
    const mutationFn = async (variables: HMIExportActiveUserReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`analytics/school/active-user-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return response.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'active_user_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

const useExportUserScoresReportForSchool = () => {
    const mutationFn = async (variables: HMIExportUserScoresReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`analytics/school/user-score-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return response.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'user_scores_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

const useExportInterviewReportForSchool = () => {
    const mutationFn = async (variables: HMIExportInterviewReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`analytics/school/interview-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return response.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'interview_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

const useExportActiveUserDemographicsReportForSchool = () => {
    const mutationFn = async (variables: HMIExportActiveUserDemographicsReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`analytics/school/active-user-demographics-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return response.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'active_user_demographics_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

const useExportUserJobsReportForSchool = () => {
    const mutationFn = async (variables: HMIExportUserJobsReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`analytics/school/user-jobs-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return response.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'user_jobs_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

const useExportUserVisitsReportForSchool = () => {
    const mutationFn = async (variables: HMIExportUserVisitsReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`analytics/school/user-visits-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return response.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'user_visits_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

const useExportPageViewsReportForSchool = () => {
    const mutationFn = async (variables: HMIExportPageViewsReportForSchoolData) => {
        const token = cookiesHelper.getCookie((process.env.REACT_APP_AUTH_TOKEN_NAME as string));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get(`analytics/school/page-views-report/csv?${variables.payload.filterQuery}`, { responseType: 'stream' });
        return response.data;
    };

    return useMutation({
        mutationFn,
        retry: 0,
        onSuccess: async (data) => {
            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = 'page_views_report.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        onError: () => { },
    });
};

export {
    useGetUserSignUpReportForSchool,
    useGetActiveUserReportForSchool,
    useGetUserScoreReportForSchool,
    useGetInterviewReportForSchool,
    useGetActiveUserDemographicsReportForSchool,
    useGetUserJobsReportForSchool,
    useGetUserVisitsReportForSchool,
    useGetPageViewsReportForSchool,
    useGetCertificateAwardsReportForSchool,
    useExportCertificateAwardsReportForSchool,
    useExportUserSignUpReportForSchool,
    useExportActiveUserReportForSchool,
    useExportUserScoresReportForSchool,
    useExportInterviewReportForSchool,
    useExportActiveUserDemographicsReportForSchool,
    useExportUserJobsReportForSchool,
    useExportUserVisitsReportForSchool,
    useExportPageViewsReportForSchool,
};